/** The tractor beam, capture and rescue (docs/spec/02 §6). */

import { clamp, DOWN, lerp } from './math';
import { losePowers } from './powerups';
import { findEnemy, makeEnemy } from './roster';
import { BEAM, FIELD, FIGHTER, NOVA, SCORE } from './tuning';
import type { Beam, Enemy, GameState } from './types';

function beamOff(s: GameState, beam: Beam): void {
  s.beam = null;
  s.events.push({ type: 'beam', on: false, x: beam.x, y: beam.y });
}

/** The cone's half width at height `y`, or -1 above or below it. */
export function beamHalfWidth(beam: Beam, y: number): number {
  const rel = (y - beam.y) / BEAM.length;
  if (rel < 0 || rel > beam.reach + 0.12) return -1;
  return BEAM.topHalf + (BEAM.bottomHalf - BEAM.topHalf) * clamp(rel, 0, 1);
}

/** The Monarch gives up the run and carries on down and out. */
function moveOn(boss: Enemy): void {
  boss.state = 'leaving';
  boss.after = 'wrap';
  boss.heading = DOWN;
  boss.timer = 0;
}

/** The fighter has reached the Monarch: it becomes a captive in the slot above it. */
function capture(s: GameState, boss: Enemy): void {
  const p = s.player;
  const c = makeEnemy(s, 'captive', p.caughtX, p.caughtY);
  c.slotted = true;
  c.row = -1;
  c.col = boss.col;
  c.wave = boss.wave;
  c.state = 'homing';
  c.speed = boss.speed;
  c.group = boss.group;
  c.partner = boss.id;
  boss.partner = c.id;
  boss.state = 'homing';
  boss.timer = 0;
  s.enemies.push(c);
  // Not an explosion: the ship is simply no longer the player's. The next one arrives as after a death.
  p.status = 'dead';
  p.timer = 0;
  p.dual = false;
  // Its powers and the chain go with it, as with any ship lost (03 §2, §4).
  s.chain = 0;
  losePowers(s);
  s.capturedMsg = BEAM.capturedMsg;
  s.events.push({ type: 'captured' });
}

function tryCatch(s: GameState, beam: Beam): void {
  const p = s.player;
  if (p.status !== 'flying' || p.dual || p.safe > 0) return;
  const half = beamHalfWidth(beam, FIELD.fighterY);
  if (half < 0 || Math.abs(p.x - beam.x) > half) return;
  if (s.powers.shield) {
    // The shield takes it instead: it bursts and the beam lets go (03 §2).
    s.powers.shield = false;
    p.safe = NOVA.safeTicks;
    s.events.push({ type: 'shieldBurst', x: p.x, y: FIELD.fighterY });
    beam.phase = 'retract';
    beam.t = 0;
    return;
  }
  p.status = 'caught';
  p.timer = 0;
  p.caughtX = p.x;
  p.caughtY = FIELD.fighterY;
  p.spin = 0;
  s.events.push({ type: 'caught', x: p.x, y: FIELD.fighterY });
}

export function updateBeam(s: GameState): void {
  const p = s.player;
  if (!s.beam) {
    const boss = s.enemies.find((e) => e.state === 'hover' && !e.gone);
    if (!boss) return;
    s.beam = { boss: boss.id, phase: 'extend', t: 0, x: boss.x, y: boss.y + 8, reach: 0 };
    s.events.push({ type: 'beam', on: true, x: boss.x, y: boss.y });
    return;
  }
  const beam = s.beam;
  const boss = findEnemy(s, beam.boss);
  if (!boss || boss.state !== 'hover') {
    // Its Monarch was shot out from over it: the beam dies and a ship in it drops free.
    if (p.status === 'caught') {
      p.status = 'flying';
      p.timer = 0;
    }
    beamOff(s, beam);
    return;
  }
  beam.t++;
  if (p.status === 'caught') {
    const f = Math.min(1, p.timer / BEAM.rise);
    p.caughtX = lerp(p.x, boss.x, f);
    p.caughtY = lerp(FIELD.fighterY, boss.y + 16, f);
    p.spin += 0.4;
    if (f >= 1) {
      capture(s, boss);
      beamOff(s, beam);
    }
    return;
  }
  if (beam.phase === 'extend') {
    beam.reach = beam.t / BEAM.extend;
    if (beam.t >= BEAM.extend) {
      beam.phase = 'hold';
      beam.t = 0;
    }
  } else if (beam.phase === 'hold') {
    beam.reach = 1;
    tryCatch(s, beam);
    if (beam.phase === 'hold' && beam.t >= BEAM.hold) {
      beam.phase = 'retract';
      beam.t = 0;
    }
  } else {
    beam.reach = 1 - beam.t / BEAM.retract;
    if (beam.t >= BEAM.retract) {
      beamOff(s, beam);
      moveOn(boss);
    }
  }
}

/** Its Monarch was shot down in flight: the captive is the player's again. */
export function startRescue(s: GameState, captive: Enemy): void {
  captive.gone = true;
  s.rescue = { x: captive.x, y: captive.y, t: 0, phase: 'spin', spin: 0, side: 0 };
  s.events.push({ type: 'rescued', x: captive.x, y: captive.y });
}

/** The rescued ship takes its place: beside the fighter, or in reserve if there is none to join. */
function dock(s: GameState, x: number): void {
  const p = s.player;
  const flying = p.status === 'flying' || p.status === 'arriving';
  if (flying && !p.dual) {
    p.dual = true;
    // The pair's centre is midway between the two ships.
    p.x = clamp((p.x + x) / 2, FIGHTER.margin + FIGHTER.dualOffset, FIELD.w - FIGHTER.margin - FIGHTER.dualOffset);
  } else if (s.lives < SCORE.maxLives) s.lives++;
  s.rescue = null;
  s.events.push({ type: 'docked', x, y: FIELD.fighterY });
}

export function updateRescue(s: GameState): void {
  const r = s.rescue;
  if (!r) return;
  r.t++;
  const p = s.player;
  if (r.phase === 'spin') {
    r.spin += 0.35;
    if (r.t >= BEAM.rescueSpin) {
      r.phase = 'fly';
      r.spin = 0;
      r.t = 0;
      // The side nearer the middle of the screen, settled now: a fighter flicking across the
      // centre line must not send it back and forth.
      r.side = p.x < FIELD.w / 2 ? 1 : -1;
    }
    return;
  }
  const flying = p.status === 'flying' || p.status === 'arriving';
  // To the fighter's side; to the pad if there is no fighter.
  const reach = FIGHTER.margin + FIGHTER.dualOffset * 2;
  const beside = clamp(p.x + r.side * FIGHTER.dualOffset * 2, FIGHTER.margin, FIELD.w - FIGHTER.margin);
  // Pinned against a wall, the fighter has no room on that side: take the other.
  const target = flying && !p.dual
    ? (Math.abs(beside - p.x) < FIGHTER.dualOffset * 2 ? p.x - r.side * FIGHTER.dualOffset * 2 : beside)
    : FIELD.w / 2;
  r.x += clamp(target - r.x, -BEAM.rescueSpeed, BEAM.rescueSpeed);
  r.y = Math.min(FIELD.fighterY, r.y + BEAM.rescueSpeed);
  const there = r.y >= FIELD.fighterY && Math.abs(target - r.x) <= BEAM.rescueSpeed;
  if (there || r.t >= BEAM.rescueFlyMax) dock(s, flying && there ? target : clamp(r.x, reach - FIGHTER.dualOffset * 2, FIELD.w));
}
